import React, { useContext, useLayoutEffect, useRef, useState } from 'react';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleHeader,
    CollapsibleHeaderChevron,
} from '@fiverr-private/disclosure';
import { translate } from '@fiverr-private/i18n-react';
import { Stack } from '@fiverr-private/layout_components';
import { Text } from '@fiverr-private/typography';
import { Box, theme, Types } from '@fiverr-private/theme';
import { ComplianceIcon, ComplianceServicesL2Icon } from '@fiverr-private/visuals';
import {
    ResponsiveFlexAlignmentType,
    ResponsiveFlexDirectionType,
    ResponsiveMarginType,
    ResponsiveSpacingType,
    ResponsiveWidthType,
} from '@fiverr-private/theme/src/components/Box/types';
import pathfinder from '@fiverr-private/pathfinder';
import { getContext } from '@fiverr-private/fiverr_context';
import { ComplianceType, ComplianceTypes } from '../ComplianceModal/types';
import { ComplianceContext } from '../ComplianceProvider';
import { reportEventsApi } from '../../biEvents';
import { useSellerConfigData } from '../../api/useSellerConfigData';
import { AgencyFirstTimeComplianceSection } from '../AgencyFirstTimeComplianceSection';
import { FirstTimeComplianceCard } from './FirstTimeComplianceCard';
import { LegalDocsSection } from './LegalDocsSection';
import { BackgroundCheckSection } from './BackgroundCheckSection';
import { WorkforceClassificationSection } from './WorkforceClassificationSection';

type BodySize = keyof typeof theme.bodySizes;
const COMPLIANCE_SECTION_SMALL_SCREEN_WIDTH = 600;

interface CSSProperties {
    width?: ResponsiveWidthType;
    boxPadding?: ResponsiveSpacingType;
    complianceItemsDirections?: ResponsiveFlexDirectionType;
    complianceItemsGap?: ResponsiveSpacingType;
    arrowRightSize?: string;
    complianceItemsPaddingX?: ResponsiveSpacingType;
    complianceItemsMarginBottom?: string;
    complianceItemsMarginTop?: ResponsiveMarginType;
    textSizeTitle?: Types.ResponsiveObject<BodySize>;
    headerAlignItems?: ResponsiveFlexAlignmentType;
    headerStackPadding?: ResponsiveSpacingType;
}

function getCSS(viewType: string, isMobile?: boolean): CSSProperties {
    if (viewType === 'inbox') {
        return {
            boxPadding: '0',
            complianceItemsDirections: 'column',
            complianceItemsGap: '3',
            arrowRightSize: 'sm',
            complianceItemsPaddingX: '5',
            complianceItemsMarginBottom: '4',
            textSizeTitle: 'b_sm',
            headerAlignItems: 'flexStart',
            headerStackPadding: '5',
        };
    } else {
        return {
            boxPadding: '5',
            complianceItemsDirections: isMobile ? 'column' : 'row',
            complianceItemsGap: '4',
            complianceItemsMarginTop: '1',
            arrowRightSize: 'md',
            textSizeTitle: 'b_md',
            headerAlignItems: 'center',
            headerStackPadding: '0',
        };
    }
}

interface FirstTimeComplianceSectionProps {
    viewType?: string;
    requeseteeId: number;
    requeseteeUserName: string;
}

export const FirstTimeComplianceSection: React.FC<FirstTimeComplianceSectionProps> = ({
    viewType = '',
    requeseteeId,
    requeseteeUserName,
}) => {
    const [click, setClick] = useState(viewType === 'inbox');
    const complianceContext = useContext(ComplianceContext);
    const {
        sellerConfigs,
        shouldShowWorkforceClassification,
        shouldShowBackgroundCheck,
        shouldShowLegalDocs,
        isEligibleForBackgroundCheck,
    } = useSellerConfigData(requeseteeId);

    const openModalOptions = {
        sellerName: requeseteeUserName,
        sellerId: requeseteeId,
    };

    const handleBoxClick = async (complianceType: ComplianceType) => {
        if (complianceType === ComplianceTypes.BACKGROUND_CHECK) {
            return complianceContext?.openBackgroundCheckModal(openModalOptions);
        }

        if (complianceType === ComplianceTypes.WORKFORCE_CLASSIFICATION) {
            return complianceContext?.openWFCModal(openModalOptions);
        }

        if (complianceType === ComplianceTypes.LEGAL_DOCS) {
            return complianceContext?.openSendLegalDocsModal(openModalOptions);
        }
    };

    const observedElementRef = useRef(null);

    const [width, setWidth] = useState(0);
    const showInColumn = !!width && width < COMPLIANCE_SECTION_SMALL_SCREEN_WIDTH;

    useLayoutEffect(() => {
        if (observedElementRef.current) {
            const observer = new ResizeObserver((entries) => {
                requestAnimationFrame(() => {
                    const entry = entries?.[0];
                    if (!entry && !width) return;

                    if (width !== entry.contentRect.width && observedElementRef.current) {
                        setWidth(entry.contentRect.width);
                    }
                });
            });

            observer.observe(observedElementRef.current);

            // Cleanup function
            return () => {
                observer.disconnect();
            };
        }
    }, [width]);

    const legalDocsConfigurations = sellerConfigs?.legalDocsData;

    const isAllowedToUpload = legalDocsConfigurations?.allowedActions?.isAllowedToUpload;
    const totalTemplates = complianceContext?.complianceData?.legalDocsData?.totalTemplates;
    const redirectUrl = isAllowedToUpload && !totalTemplates ? pathfinder('legal_documents_page_render') : undefined;

    const { isMobile } = getContext();

    const isLegalDocsFirstTime =
        shouldShowLegalDocs && !shouldShowBackgroundCheck && !shouldShowWorkforceClassification;

    if (isLegalDocsFirstTime) {
        return (
            <AgencyFirstTimeComplianceSection
                viewType={viewType}
                requeseteeId={requeseteeId}
                requeseteeUserName={requeseteeUserName}
            />
        );
    }

    const complianceItems: {
        icon: React.ReactNode;
        complianceType: ComplianceType;
        Wrapper: React.FC<{ children: React.ReactNode }>;
    }[] = [
        ...LegalDocsSection(redirectUrl, requeseteeId, requeseteeUserName),
        ...BackgroundCheckSection(isEligibleForBackgroundCheck, requeseteeId),
        ...WorkforceClassificationSection(requeseteeId),
    ];

    const {
        boxPadding,
        complianceItemsDirections,
        complianceItemsPaddingX,
        textSizeTitle,
        headerAlignItems,
        headerStackPadding,
        complianceItemsGap,
        complianceItemsMarginTop,
    } = getCSS(viewType, isMobile || showInColumn);

    return (
        <Box
            ref={observedElementRef}
            backgroundColor="grey_100"
            borderRadius="lg"
            borderColor="grey_400"
            borderWidth="sm"
            paddingY="1"
            paddingX={boxPadding}
            borderStyle="solid"
            width={'100%'}
        >
            <Collapsible
                expanded={click}
                onToggle={() => {
                    setClick(!click);
                    if (!click && !!complianceContext) {
                        reportEventsApi.clickedOnExpandedFirstTimeBanner({
                            pageName: complianceContext.pageName,
                        });
                    }
                }}
            >
                <CollapsibleHeader color={'grey_1200'}>
                    <Stack
                        direction="row"
                        justifyContent="spaceBetween"
                        alignItems={headerAlignItems}
                        width="100%"
                        paddingX={headerStackPadding}
                        gap="1"
                        id="philip"
                    >
                        <Stack padding={'0'} gap={viewType === 'inbox' ? '2' : '5'} display="inlineFlex" width={'100%'}>
                            <Box display="inlineFlex">
                                {viewType === 'inbox' ? (
                                    <ComplianceIcon size="md" />
                                ) : (
                                    <ComplianceServicesL2Icon size="md" />
                                )}
                            </Box>
                            <Stack direction="column" gap="1" width={'100%'} paddingBottom="0">
                                <Stack flexGrow={1} justifyContent={'spaceBetween'}>
                                    <Text
                                        size={textSizeTitle}
                                        fontWeight="bold"
                                        paddingTop={viewType === 'inbox' ? '0' : '1'}
                                    >
                                        {translate('compliance.first_time_compliance_section.title')}
                                    </Text>
                                    <Stack alignSelf={'flexStart'} justifySelf={'flexEnd'}>
                                        <CollapsibleHeaderChevron />
                                    </Stack>
                                </Stack>
                                {viewType !== 'inbox' && (
                                    <Text size="b_sm" color="bodySecondary">
                                        {translate('compliance.first_time_compliance_section.body')}
                                    </Text>
                                )}
                            </Stack>
                        </Stack>
                    </Stack>
                </CollapsibleHeader>
                <CollapsibleContent>
                    <Stack
                        width="calc(100% - 2px)"
                        gap={complianceItemsGap}
                        direction={complianceItemsDirections}
                        marginTop={complianceItemsMarginTop}
                        paddingX={complianceItemsPaddingX}
                        paddingTop="1"
                        marginBottom="4"
                    >
                        {complianceItems.map(({ Wrapper, ...item }) => (
                            <Wrapper key={item.complianceType}>
                                <FirstTimeComplianceCard
                                    onClick={() => handleBoxClick(item.complianceType)}
                                    key={item.complianceType}
                                    icon={item.icon}
                                    title={translate(
                                        `compliance.first_time_compliance_section.types.${
                                            viewType === 'inbox' ? `inbox.${item.complianceType}` : item.complianceType
                                        }`
                                    )}
                                    ctaText={translate(
                                        `compliance.first_time_compliance_section.actions.${item.complianceType}`
                                    )}
                                />
                            </Wrapper>
                        ))}
                    </Stack>
                </CollapsibleContent>
            </Collapsible>
        </Box>
    );
};
